import { useCallback, useEffect, useRef, useState } from 'react';

import { onAngularLocationChangeStart } from '@/services/angularjs-bridge';

interface DamLeaveGuard {
    /** A leave waits for the user to discard or stay. */
    isAsking: boolean;
    /** Leaves at once while nothing is unsaved, otherwise asks first. */
    requestLeave: (leave: () => void) => void;
    /** Leaves without asking, e.g. after the asset was deleted. */
    leaveNow: (leave: () => void) => void;
    /** Gives up the unsaved state and carries out the waiting leave. */
    discard: () => void;
    /** Keeps the unsaved state and drops the waiting leave. */
    stay: () => void;
}

const pathOf = (url: string) => new URL(url, window.location.href).pathname;

/**
 * Asks before the page is left with unsaved changes (D-049): through the page's own controls
 * (`requestLeave`), AngularJS navigation and the browser's back and forward buttons. Reload,
 * closing the tab and other full page loads get the browser's own `beforeunload` prompt.
 * Location changes that keep the path (query string of the library) do not leave the page.
 */
function useDamLeaveGuard(hasUnsavedChanges: boolean): DamLeaveGuard {
    const [isAsking, setIsAsking] = useState(false);
    const hasUnsavedRef = useRef(hasUnsavedChanges);
    const isLeavingRef = useRef(false);
    const pendingLeaveRef = useRef<(() => void) | undefined>(undefined);

    useEffect(() => {
        hasUnsavedRef.current = hasUnsavedChanges;
    }, [hasUnsavedChanges]);

    const mustAsk = useCallback(() => hasUnsavedRef.current && !isLeavingRef.current, []);

    const ask = useCallback((leave: () => void) => {
        pendingLeaveRef.current = leave;
        setIsAsking(true);
    }, []);

    const requestLeave = useCallback((leave: () => void) => {
        if (mustAsk()) {
            ask(leave);
        } else {
            leave();
        }
    }, [ask, mustAsk]);

    const leaveNow = useCallback((leave: () => void) => {
        isLeavingRef.current = true;
        leave();
    }, []);

    const discard = useCallback(() => {
        const leave = pendingLeaveRef.current;
        pendingLeaveRef.current = undefined;
        setIsAsking(false);
        if (leave) {
            leaveNow(leave);
        }
    }, [leaveNow]);

    const stay = useCallback(() => {
        pendingLeaveRef.current = undefined;
        setIsAsking(false);
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (mustAsk()) {
                event.preventDefault();
                // Browsers that predate `preventDefault` for this event only prompt on a set returnValue.
                event.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        const unsubscribe = onAngularLocationChangeStart(({ newUrl, oldUrl, prevent, proceed }) => {
            if (!mustAsk() || pathOf(newUrl) === pathOf(oldUrl)) {
                return;
            }
            prevent();
            ask(proceed);
        });
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            unsubscribe?.();
        };
    }, [ask, mustAsk]);

    return { isAsking, requestLeave, leaveNow, discard, stay };
}

export { useDamLeaveGuard };
export type { DamLeaveGuard };
